/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { LookupErrorCode } from "./LookupErrorCode";
import throttle from "./throttle";
var LookupControl = /** @class */ (function () {
    function LookupControl(view, form, fieldId, lookupProvider, logger) {
        var _this = this;
        this.view = view;
        this.form = form;
        this.fieldId = fieldId;
        this.lookupProvider = lookupProvider;
        this.logger = logger;
        this.page = 1;
        this.currentRequestVersion = 0;
        this.loadComplete = Promise.resolve();
        this.activeRequestController = null;
        this.apiSearchFilter = null;
        this.refreshData = throttle(LookupControl.RefreshDataDebounceTimeout, function (showPopup, resetControl) { return __awaiter(_this, void 0, void 0, function () {
            var e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (resetControl) {
                            this.resetControl();
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, , 8]);
                        return [4 /*yield*/, this.populateViewWithItems()];
                    case 2:
                        _a.sent();
                        if (showPopup) {
                            this.view.setPopupVisible(true);
                        }
                        _a.label = 3;
                    case 3:
                        if (!this.shouldLoadMoreDataOnRefresh()) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.loadMoreData()];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 5: return [3 /*break*/, 8];
                    case 6:
                        e_1 = _a.sent();
                        this.logger.error("Error loading data");
                        return [4 /*yield*/, this.view.onError("LookupGenericError", this.onErrorRefresh)];
                    case 7:
                        _a.sent();
                        return [3 /*break*/, 8];
                    case 8: return [2 /*return*/];
                }
            });
        }); }, true);
        var onInputChange = function (showPopup, resetControl) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.refreshData(showPopup, resetControl)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); };
        var onRefreshChildrenItems = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.apiSearchFilter = null;
                        this.view.clearSelectedItem();
                        this.invalidatePendingRequests();
                        return [4 /*yield*/, this.refreshData(false, true)];
                    case 1:
                        _a.sent();
                        this.view.updateInitDataLoadedState(true);
                        return [2 /*return*/];
                }
            });
        }); };
        this.view.registerOnInputChange(onInputChange);
        this.view.registerOnLoadMoreData(function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, this.loadMoreData()];
                case 1: return [2 /*return*/, _a.sent()];
            }
        }); }); });
        this.view.registerOnRefreshChildrenElements(onRefreshChildrenItems);
        this.onErrorRefresh = function () { return _this.view.onRetry(function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, this.retry()];
                case 1: return [2 /*return*/, _a.sent()];
            }
        }); }); }); };
        if (!this.view.inputValue) {
            this.setDefaultValue();
        }
    }
    LookupControl.prototype.invalidatePendingRequests = function () {
        this.currentRequestVersion++;
        if (this.activeRequestController) {
            this.activeRequestController.abort();
            this.activeRequestController = null;
        }
    };
    LookupControl.prototype.resetControl = function () {
        this.loadingMoreData = false;
        this.retryNeeded = false;
        this.lastResponse = null;
        this.lastSuccessfulResponse = null;
        this.page = 1;
        this.view.resetControl();
    };
    LookupControl.prototype.retry = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.retryNeeded = false;
                        if (!(this.page > 1)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.refreshData(true, false)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, this.refreshData(true, true)];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.hasAnyData = function (response) {
        var _a;
        return ((_a = response === null || response === void 0 ? void 0 : response.items) === null || _a === void 0 ? void 0 : _a.length) > 0;
    };
    LookupControl.prototype.shouldLoadMoreDataOnRefresh = function () {
        return this.lastResponse && this.hasAnyData(this.lastResponse) && this.view.IsPopupListAtTheEndOfScroll();
    };
    LookupControl.prototype.loadMoreData = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.loadingMoreData === true || this.retryNeeded === true || !this.lastResponse || !this.hasAnyData(this.lastResponse)) {
                            return [2 /*return*/];
                        }
                        this.loadingMoreData = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 3, 4]);
                        return [4 /*yield*/, this.populateViewWithItems()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        this.loadingMoreData = false;
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.syncSelection = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, e_2;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.loadComplete];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _a = _b.sent();
                        return [3 /*break*/, 3];
                    case 3:
                        _b.trys.push([3, 5, , 6]);
                        // Note: view.selectedItem?.value is returned empty from the view, 
                        // we check for any selected item presence, otherwise lookup may send invalid value
                        if (this.view.selectedItem || !this.view.inputValue) {
                            return [2 /*return*/];
                        }
                        // input somehow got a value, either quick autofill or from a script, let's emulate set by api
                        return [4 /*yield*/, this.filterRecordsFromApiSearch(this.view.inputValue)];
                    case 4:
                        // input somehow got a value, either quick autofill or from a script, let's emulate set by api
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        e_2 = _b.sent();
                        this.logger.error(e_2);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.loadItems = function (lookupRequest) {
        var result = this.loadItemsImpl(lookupRequest);
        this.loadComplete = result.then(function (x) { });
        return result;
    };
    LookupControl.prototype.loadItemsImpl = function (lookupRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var version, response, e_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        version = ++this.currentRequestVersion;
                        this.lastRequest = lookupRequest;
                        if (this.activeRequestController) {
                            this.activeRequestController.abort();
                        }
                        this.activeRequestController = new AbortController();
                        this.view.onLoadingStarted();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 6]);
                        return [4 /*yield*/, this.lookupProvider.getItems(this.form, this.fieldId, lookupRequest, this.activeRequestController.signal)];
                    case 2:
                        response = _a.sent();
                        return [4 /*yield*/, this.handleServerNormalResponse(response, version)];
                    case 3: return [2 /*return*/, _a.sent()];
                    case 4:
                        e_3 = _a.sent();
                        return [4 /*yield*/, this.handleServerErrorResponse(e_3)];
                    case 5:
                        _a.sent();
                        throw e_3;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.handleServerNormalResponse = function (response, version) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!response)
                            return [2 /*return*/, response];
                        if (!(response.errorCode === LookupErrorCode.Success)) return [3 /*break*/, 1];
                        if (version !== this.currentRequestVersion) {
                            this.logger.log("Stale response received, ignoring.");
                            return [2 /*return*/];
                        }
                        this.lastResponse = response;
                        this.lastSuccessfulResponse = this.lastResponse;
                        return [3 /*break*/, 3];
                    case 1:
                        this.logger.error("There was a problem retrieving items. Try again later.");
                        this.retryNeeded = true;
                        return [4 /*yield*/, this.view.onError("LookupGenericError", this.onErrorRefresh)];
                    case 2:
                        _a.sent();
                        this.lastResponse = null;
                        _a.label = 3;
                    case 3:
                        this.view.onLoadingFinished();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    LookupControl.prototype.handleServerErrorResponse = function (e) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!e.status) return [3 /*break*/, 4];
                        this.logger.error(e);
                        this.retryNeeded = true;
                        if (!(e.status === 429)) return [3 /*break*/, 2];
                        this.logger.error("The server is busy right now and not all values are being retrieved. Try again later.");
                        return [4 /*yield*/, this.view.onError("LookupRequestThrottled", this.onErrorRefresh)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, this.view.onError("LookupGenericError", this.onErrorRefresh)];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4:
                        if (!e.aborted) {
                            this.view.onLoadingFinished();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.getLookupRequest = function (searchText) {
        if (searchText === void 0) { searchText = null; }
        var pagingInfos = null;
        if (this.lastSuccessfulResponse) {
            pagingInfos = this.lastSuccessfulResponse.items.map(function (item) {
                return {
                    entityName: item.entityName,
                    pagingCookie: item.pagingCookie
                };
            });
        }
        var request = {
            searchText: searchText !== null && searchText !== void 0 ? searchText : this.view.inputValue,
            page: this.page,
            pagingInfos: pagingInfos
        };
        var parentInformation = this.view.getParentValue();
        if (!!parentInformation) {
            request.parentLookupValue = parentInformation;
        }
        return request;
    };
    LookupControl.prototype.getAllItems = function (response) {
        response.items.forEach(function (items) {
            items.items.forEach(function (item) { return (item.entityName = items.entityName); });
        });
        return response.items
            .map(function (r) { return r.items; })
            .reduce(function (a, b) { return a.concat(b); }, []);
    };
    LookupControl.prototype.populateViewWithItems = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.loadItems(this.getLookupRequest())];
                    case 1:
                        response = _a.sent();
                        if (!response)
                            return [2 /*return*/];
                        if (response.errorCode === LookupErrorCode.Success) {
                            this.page++;
                            this.view.appendItems(this.getAllItems(response));
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.setDefaultValue = function () {
        return __awaiter(this, void 0, void 0, function () {
            var defaultValue, lookupReq, parentInformation, response, lookUpValue, lookupEntries;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        defaultValue = this.view.getDefaultValue();
                        if (!defaultValue || !!this.apiSearchFilter) {
                            return [2 /*return*/];
                        }
                        lookupReq = { searchText: defaultValue, page: 1, pagingInfos: null };
                        parentInformation = this.view.getParentValue();
                        if (!!parentInformation) {
                            lookupReq.parentLookupValue = parentInformation;
                        }
                        return [4 /*yield*/, this.loadItems(lookupReq)];
                    case 1:
                        response = _a.sent();
                        if (!response)
                            return [2 /*return*/];
                        if (response.errorCode === LookupErrorCode.Success) {
                            lookupEntries = this.getAllItems(response);
                            lookUpValue = lookupEntries.find(function (lookupEntry) { return lookupEntry.value === defaultValue; });
                        }
                        if (lookUpValue) {
                            this.view.setDefaultItem(lookUpValue);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    LookupControl.prototype.filterRecordsFromApiSearch = function (searchText) {
        return __awaiter(this, void 0, void 0, function () {
            var response, lookupEntries, e_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.apiSearchFilter = searchText;
                        this.invalidatePendingRequests();
                        this.resetControl();
                        this.view.clearSelectedItem();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, , 7]);
                        return [4 /*yield*/, this.loadItems(this.getLookupRequest(searchText))];
                    case 2:
                        response = _a.sent();
                        if (!response)
                            return [2 /*return*/];
                        if (response.errorCode !== LookupErrorCode.Success) {
                            this.logger.error("Lookup failed with error code: " + response.errorCode);
                            return [2 /*return*/, false];
                        }
                        lookupEntries = this.getAllItems(response);
                        if (lookupEntries.length == 1) {
                            this.view.setDefaultItem(lookupEntries[0]);
                        }
                        this.page++;
                        this.view.appendItems(lookupEntries);
                        _a.label = 3;
                    case 3:
                        if (!this.shouldLoadMoreDataOnRefresh()) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.loadMoreData()];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 5: return [2 /*return*/, true];
                    case 6:
                        e_4 = _a.sent();
                        this.logger.error("Error loading data");
                        return [2 /*return*/, false];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    ;
    LookupControl.RefreshDataDebounceTimeout = 200;
    return LookupControl;
}());
export { LookupControl };
